// Operator behaviour for Attribute Conditions. The allowed operators per field come
// from the customer_profiles fields API; this file only decides what value each needs.

// Used when a field is not in the fetched list (e.g. API failed or an old saved field).
export const DEFAULT_OPERATORS = ['=', '>=', '<=', '>', '<', '!=', 'datediff >='];

// Operators that compare against nothing: no value input, sent as value: null.
const NO_VALUE_OPERATORS = ['is_today', 'year = current', 'year != current', 'is_set', 'is_not_set'];

// Operators whose value is a number of days, whatever the field type.
const DAY_OPERATORS = ['within_n_days', 'datediff >='];

// For these fields 'datediff >=' is entered in months; the API still gets days.
const MONTH_FIELDS = ['anniversary_gwp_redeemed_date', 'birthday_reward_redeemed_date'];
const DAYS_PER_MONTH = 30;

export const isMonthCondition = (field, operator) => operator === 'datediff >=' && MONTH_FIELDS.includes(field);

export const monthsToDays = (months) => Math.round(Number(months) * DAYS_PER_MONTH);

// "60" -> "2"; a non-whole result keeps up to 2 decimals.
export const daysToMonths = (days) => String(Math.round((Number(days) / DAYS_PER_MONTH) * 100) / 100);

// 'none' | 'between' | 'list' | 'days' | 'single'
export const getOperatorValueKind = (operator) => {
  if (NO_VALUE_OPERATORS.includes(operator)) return 'none';
  if (operator === 'between') return 'between';
  if (operator === 'is_one_of') return 'list';
  if (DAY_OPERATORS.includes(operator)) return 'days';
  return 'single';
};

// Starting value for a row; boolean fields default to 'true'.
export const getEmptyConditionValue = (fieldType, operator) => {
  const kind = getOperatorValueKind(operator);
  if (kind === 'between') return ['', ''];
  if (kind === 'list') return [];
  if (fieldType === 'boolean' && kind !== 'none') return 'true';
  return '';
};

// Attribute rules: rows in the same group can't repeat a field, and a nested group can't
// use a field from any of its parent groups (and vice versa). Other branches may reuse it.
export const getGroupRowFields = (group = {}) => [...(group.conditions || []), ...(group.afterConditions || [])]
  .map((row) => row.field)
  .filter(Boolean);

export const getSubtreeFields = (group = {}) => [
  ...getGroupRowFields(group),
  ...(group.children || []).flatMap(getSubtreeFields),
];

export const hasDuplicateConditionField = (groups = [], ancestorFields = []) => (groups || []).some((group) => {
  const fields = getGroupRowFields(group);
  const isDuplicate = fields.some((field, index) => ancestorFields.includes(field) || fields.indexOf(field) !== index);
  if (isDuplicate) return true;
  return hasDuplicateConditionField(group.children, [...ancestorFields, ...fields]);
});

const isBlank = (value) => String(value ?? '').trim() === '';

export const isConditionValueMissing = (row = {}) => {
  const kind = getOperatorValueKind(row.operator);
  if (kind === 'none') return false;
  if (kind === 'between') return !Array.isArray(row.value) || row.value.length < 2 || row.value.some(isBlank);
  if (kind === 'list') return !Array.isArray(row.value) || !row.value.some((item) => !isBlank(item));
  return isBlank(row.value);
};
